import { createContext, useContext, ReactNode, useMemo } from "react";

import { BRANDING_VARS } from "@smart/itops-sb-design-system-dom";

export type Branding = {
  brandColor?: string;
  affectedAreas?: {
    headerBackground?: boolean;
    headerUnderline?: boolean;
    firmName?: boolean;
    sectionTabHighlights?: boolean;
    sectionHeadings?: boolean;
    buttons?: boolean;
  };
  headerTextColor?: string;
  backgroundColor?: string;
  headerDisplay?: {
    firmLogo?: boolean;
    firmName?: boolean;
  };
  buttonRadius?: string;
  inputRadius?: string;
  logo?: string;
};

const BrandingContext = createContext<Branding | undefined>(undefined);

const darkenColor = (color: string, percentage: number): string => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const factor = 1 - percentage / 100;
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);

  return `#${[newR, newG, newB]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")}`;
};

const colorWithOpacity = (color: string, opacity: number): string => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const convertInputRadius = (radius?: string): string => {
  switch (radius) {
    case "square":
      return "0rem";
    case "slightlyRounded":
      return "0.4rem";
    case "moderatelyRounded":
      return "0.8rem";
    case "fullyRounded":
      return "2rem";
    default:
      return "0.8rem";
  }
};

const getButtonRadius = (buttonRadius?: string) => {
  switch (buttonRadius) {
    case "square":
      return "0rem";
    case "slightlyRounded":
      return "0.4rem";
    case "moderatelyRounded":
      return "0.8rem";
    case "fullyRounded":
      return "2rem";
    default:
      return "0.8rem";
  }
};

export const BrandingProvider = ({
  children,
  branding,
}: {
  children: ReactNode;
  branding?: Branding;
}) => {
  const cssVariables = useMemo(() => {
    // Default blue colors from the design system
    const blue60 = "#E1EEFF";
    const blue460 = "#177FFD";
    const blue560 = "#0265DC";
    const blue660 = "#024FAC";

    const baseVariables = {
      [BRANDING_VARS.brandColor]: branding?.brandColor || blue560,
      [BRANDING_VARS.buttonBrandRadius]: getButtonRadius(
        branding?.buttonRadius,
      ),
      [BRANDING_VARS.inputBrandRadius]: convertInputRadius(
        branding?.inputRadius,
      ),
    };

    if (branding?.brandColor && branding?.affectedAreas?.buttons) {
      const { brandColor } = branding;
      return {
        ...baseVariables,
        [BRANDING_VARS.buttonBrandColor]: brandColor,
        [BRANDING_VARS.buttonBrandColorHover]: darkenColor(brandColor, 15),
        [BRANDING_VARS.buttonBrandColorActive]: darkenColor(brandColor, 25),
        [BRANDING_VARS.buttonBrandColorSubtleHover]: colorWithOpacity(
          brandColor,
          0.1,
        ),
      } as React.CSSProperties;
    }

    return {
      ...baseVariables,
      [BRANDING_VARS.buttonBrandColor]: blue560,
      [BRANDING_VARS.buttonBrandColorHover]: blue460,
      [BRANDING_VARS.buttonBrandColorActive]: blue660,
      [BRANDING_VARS.buttonBrandColorSubtleHover]: blue60,
    } as React.CSSProperties;
  }, [
    branding?.brandColor,
    branding?.affectedAreas?.buttons,
    branding?.buttonRadius,
    branding?.inputRadius,
  ]);

  return (
    <BrandingContext.Provider value={branding}>
      <div
        data-testid="branding-provider"
        style={{ ...cssVariables, display: "contents" }}
      >
        {children}
      </div>
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  return context;
};
